import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import classNames from "classnames";
import { makeStyles } from "@material-ui/styles";
import Drawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";
import FilterIcon from "@material-ui/icons/FilterList";
import { SpinnerContainer } from "../Common/UIComponents";
import BusySpinner from "../Common/busySpinner";
import CoursesFilter from "./coursesFilter";
import CoursesList from "./coursesList";
import { UnstyledButton } from "@mantine/core";

const useStyles = makeStyles((theme) => ({
  courses: {
    background: theme.app.background[0],
    color: theme.app.color,
  },
  header: {
    background: theme.app.background[1],
    color: theme.app.labelColor,
    borderBottom: `1px solid ${theme.app.borderColor}`,
    height: "80px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    alignItems: "center",
    padding: "0 16px",
    "@media(max-width: 600px)": {
      height: "64px",
    },
  },
  headerText: {
        fontSize: "1.35rem",
    fontWeight: 500,
  },
  drawerPaper: {
    width: "300px",
    "@media(max-width: 600px)": {
      width: "100%",
    },
  },
  filterButtonContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  filterButton: {
    color: theme.app.labelColor,
    padding: "4px",
    borderRadius: "50%",
    transition: theme.transitions.create(["background-color"], {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeInOut,
    }),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  content: {
    // height: `${theme.height - 144}px`,
    overflow: "auto",
    "@media(max-width: 600px)": {
      // height: `${theme.height - 184}px`,
      overflowY: "scroll",
      "-webkit-overflow-scrolling": "touch",
    },
  },
}));

const query = gql`
  query CourseQuery {
    courses {
      id
      isFull
      totalBooked
      totalEnqueued
      placesLeft
      placesLeftInQueue
      firstOccasion
      lastOccasion
      canReserve {
        reserve
        enqueue
        reason
        persons
      }
      maxParticipants
      maxQueueSlots
      activity {
        id
        name
        infoText
        tags
        color
        resourceAssignments {
          resource {
            name
          }
          role
        }
        location {
          id
          name
        }
        price {
          inclVat
          priceLevelId
          priceLevelName
        }
        occasions {
          id
          at
          duration
        }
      }
    }
    manages {
      id
      name
      email
      civicRegistrationNumber
      photo
    }
  }
`;

const Courses = () => {
  const classes = useStyles();
  const { language, auth } = useSelector((state) => ({
    language: state.language,
    auth: state.oidc.user !== null && !state.oidc.user.expired,
  }));
  const [filterOpen, setFilterOpen] = useState(false);
  const { loading, error, data, refetch } = useQuery(query);
  const openFilter = () => setFilterOpen(true);
  const closeFilter = () => setFilterOpen(false);

  if (loading) {
    return (
      <SpinnerContainer>
        <BusySpinner color="#222" />
      </SpinnerContainer>
    );
  }
  if (error) return <h1>{error.message}</h1>;
  const { courses, manages } = data && data;
  const firstCourse = courses && courses[0];
  if (
    (auth &&
      firstCourse &&
      firstCourse.canReserve.reason === "NotAuthenticated") ||
    !data.courses
  ) {
    refetch();
  }

  return (
    <main>
      <div className={classNames(classes.courses, "courses__main-container")}>
        <div
          className={classNames(classes.header, "courses__header-container")}
        >
          <Typography variant="h1" className={classes.headerText}>
            {language.courses}
          </Typography>
          <div className={classes.filterButtonContainer}>
            <UnstyledButton onClick={openFilter} className={classes.filterButton} aria-label={language.open_filter}>
              <FilterIcon />
            </UnstyledButton>
          </div>
        </div>
        <div className={classes.content}>
          <CoursesList courses={courses} manages={manages} />
        </div>
      </div>
      <Drawer
        open={filterOpen}
        onClose={closeFilter}
        classes={{
          paper: classes.drawerPaper,
        }}
        anchor="right"
      >
        <CoursesFilter courses={courses} close={closeFilter} />
      </Drawer>
    </main>
  );
};

export default Courses;
